/* VINOD banner carousel + spin wheel — Expanse 10.0
 * One instance per section. Infinite loop by cloning the slide set on both
 * sides of the original, so the track can always step in either direction.
 *
 * Mobile touch: the track follows the finger in real time (touchmove), then
 * snaps to the nearest card on release — a flick advances one card, a short
 * drag springs back. In centred mode the middle card is scaled up and its
 * neighbours are pushed back, so the focus reads as depth.
 *
 * Desktop: the same gesture with a mouse (press and drag the track) plus
 * horizontal trackpad swipes. Both reuse the touch maths, so a flick, a short
 * drag and a snap behave identically on every device — the arrows are now a
 * convenience, not the only way through the set. A drag that actually moved
 * swallows the click that follows it, so dragging across the Spin & Win card
 * never opens its popup by accident.
 *
 * No dependencies; safe to run before or after theme.js.
 */
(function () {
  'use strict';

  var STORE_KEY = 'vinod:wheel:prize';

  /* how far a drag must travel, and how fast a flick must be, to advance a card */
  var FLICK_V = 0.42;
  var DRAG_RATIO = 0.26;
  var WHEEL_STEP = 60;

  /* ---- focus + drag styles, injected once so no extra asset is needed ---- */
  function injectStyles() {
    if (document.getElementById('vd-bc-focus-css')) return;
    var css = [
      '[data-vd-bc] .vd-bc__viewport { touch-action: pan-y; }',
      /* the track is draggable on any pointer, so say so */
      '[data-vd-bc] .vd-bc__viewport { cursor: grab; }',
      '[data-vd-bc].vd-bc--dragging .vd-bc__viewport { cursor: grabbing; }',
      '.vd-bc--dragging, .vd-bc--dragging * { user-select: none; }',
      '.vd-bc--dragging .vd-bc__slide { transition: none; }',
      '[data-vd-bc] .vd-bc__slide img { -webkit-user-drag: none; user-select: none; }',
      '@media (max-width: 768px) {',
      '  .vd-bc__track.is-animating { transition: transform .40s cubic-bezier(.22,.61,.36,1); }',
      '  .vd-bc--focus .vd-bc__slide {',
      '    transition: transform .34s cubic-bezier(.22,.61,.36,1), box-shadow .34s ease, opacity .34s ease;',
      '    transform-origin: center center;',
      '    transform: scale(.90);',
      '    opacity: .68;',
      '  }',
      '  .vd-bc--focus .vd-bc__slide.is-near { transform: scale(.94); opacity: .84; }',
      '  .vd-bc--focus .vd-bc__slide.is-active {',
      '    transform: scale(1.05);',
      '    opacity: 1;',
      '    z-index: 2;',
      '    box-shadow: 0 16px 36px rgba(49,54,82,.28), 0 3px 10px rgba(49,54,82,.14);',
      '  }',
      '  .vd-bc--dragging .vd-bc__slide { transition: none; }',
      '}',
      '@media (prefers-reduced-motion: reduce) {',
      '  .vd-bc--focus .vd-bc__slide { transition: none; }',
      '}'
    ].join('\n');
    var tag = document.createElement('style');
    tag.id = 'vd-bc-focus-css';
    tag.appendChild(document.createTextNode(css));
    document.head.appendChild(tag);
  }

  function VinodCarousel(root) {
    this.root = root;
    this.viewport = root.querySelector('[data-vd-bc-viewport]');
    this.track = root.querySelector('[data-vd-bc-track]');
    if (!this.track) return;

    this.originals = Array.prototype.slice.call(this.track.children);
    this.count = this.originals.length;
    if (!this.count) return;

    this.autoplay = root.dataset.autoplay === 'true';
    this.speed = parseInt(root.dataset.speed, 10) || 4000;
    this.centered = root.dataset.centered === 'true';
    this.index = this.count; // start inside the middle copy
    this.animating = true;
    this.held = false;
    this.timer = null;
    this.drag = { on: false, lock: null, x0: 0, y0: 0, dx: 0, lastX: 0, tLast: 0, vx: 0 };

    injectStyles();
    if (this.centered) root.classList.add('vd-bc--focus');

    this.cloneSet();
    this.dots = Array.prototype.slice.call(root.querySelectorAll('[data-vd-bc-dot]'));
    this.bind();
    this.layout();
    this.apply(false);
    this.start();

    root.vdCarousel = this;
  }

  VinodCarousel.prototype.cloneSet = function () {
    var before = document.createDocumentFragment();
    var after = document.createDocumentFragment();
    this.originals.forEach(function (node) {
      before.appendChild(this.cloneSlide(node));
      after.appendChild(this.cloneSlide(node));
    }, this);
    this.track.insertBefore(before, this.track.firstChild);
    this.track.appendChild(after);
  };

  // Clones are decorative duplicates: strip ids/interactive wheel controls so
  // there is exactly one real wheel and no duplicate DOM ids on the page.
  VinodCarousel.prototype.cloneSlide = function (node) {
    var clone = node.cloneNode(true);
    clone.setAttribute('data-vd-clone', 'true');
    clone.setAttribute('aria-hidden', 'true');
    clone.removeAttribute('id');
    Array.prototype.forEach.call(clone.querySelectorAll('[id]'), function (el) {
      el.removeAttribute('id');
    });
    Array.prototype.forEach.call(
      clone.querySelectorAll('button, input, a, [data-vd-wheel-btn], [data-vd-wheel-gate]'),
      function (el) {
        el.setAttribute('tabindex', '-1');
        if (el.tagName === 'BUTTON' || el.tagName === 'INPUT') el.disabled = true;
        el.removeAttribute('data-vd-wheel-btn');
        el.removeAttribute('data-vd-wheel-gate');
      }
    );
    return clone;
  };

  // Shared release logic for touch and mouse: decide whether the gesture was a
  // flick, a long-enough drag, or neither, and snap accordingly.
  VinodCarousel.prototype.settleDrag = function (dx, vx) {
    var flick = Math.abs(vx) > FLICK_V;
    var steps = 0;
    if (flick) steps = vx < 0 ? 1 : -1;
    else if (Math.abs(dx) > this.step * DRAG_RATIO) steps = dx < 0 ? 1 : -1;

    if (steps) {
      this.go(steps);
    } else {
      this.apply(true);
      this.restart();
    }
  };

  VinodCarousel.prototype.bind = function () {
    var self = this;

    var prev = this.root.querySelector('[data-vd-bc-prev]');
    var next = this.root.querySelector('[data-vd-bc-next]');
    if (prev) prev.addEventListener('click', function () { self.go(-1); });
    if (next) next.addEventListener('click', function () { self.go(1); });

    this.dots.forEach(function (dot, i) {
      dot.addEventListener('click', function () {
        self.index = self.count + i;
        self.apply(true);
        self.restart();
      });
    });

    ['mouseenter', 'focusin', 'touchstart'].forEach(function (evt) {
      self.root.addEventListener(evt, function () { self.held = true; }, { passive: true });
    });
    ['mouseleave', 'focusout', 'touchend', 'touchcancel'].forEach(function (evt) {
      self.root.addEventListener(evt, function () { self.held = false; }, { passive: true });
    });

    document.addEventListener('visibilitychange', function () {
      if (document.hidden) self.stop();
      else self.start();
    });

    /* ---- touch drag: the track follows the finger ---- */
    var d = this.drag;

    this.viewport.addEventListener('touchstart', function (e) {
      if (e.touches.length > 1) return;
      var t = e.touches[0];
      d.on = true;
      d.lock = null;
      d.x0 = t.clientX;
      d.y0 = t.clientY;
      d.dx = 0;
      d.lastX = t.clientX;
      d.tLast = Date.now();
      d.vx = 0;
      clearTimeout(self._settle);
      self.normalise();
      self.track.classList.remove('is-animating');
    }, { passive: true });

    this.viewport.addEventListener('touchmove', function (e) {
      if (!d.on || e.touches.length > 1) return;
      var t = e.touches[0];
      var dx = t.clientX - d.x0;
      var dy = t.clientY - d.y0;

      if (d.lock === null) {
        if (Math.abs(dx) < 6 && Math.abs(dy) < 6) return;
        d.lock = Math.abs(dx) > Math.abs(dy) ? 'x' : 'y';
        if (d.lock === 'x') self.root.classList.add('vd-bc--dragging');
      }
      if (d.lock !== 'x') return;

      if (e.cancelable) e.preventDefault();

      var now = Date.now();
      var dt = Math.max(now - d.tLast, 1);
      d.vx = (t.clientX - d.lastX) / dt;
      d.lastX = t.clientX;
      d.tLast = now;

      // resist past the ends of the cloned run so the drag always feels anchored
      d.dx = dx;
      self.track.style.transform =
        'translateX(' + (self.offset - self.index * self.step + dx) + 'px)';
    }, { passive: false });

    function endDrag() {
      if (!d.on) return;
      d.on = false;
      self.root.classList.remove('vd-bc--dragging');

      var dx = d.dx;
      d.dx = 0;

      if (d.lock !== 'x') return;
      self.settleDrag(dx, d.vx);
    }

    this.viewport.addEventListener('touchend', endDrag, { passive: true });
    this.viewport.addEventListener('touchcancel', endDrag, { passive: true });

    /* ---- mouse drag: the same gesture with a pointer ----
       Listeners for move/up go on the document so the gesture survives the
       cursor leaving the viewport mid-drag. */
    var m = { on: false, moved: false, x0: 0, dx: 0, lastX: 0, tLast: 0, vx: 0 };

    this.viewport.addEventListener('mousedown', function (e) {
      if (e.button !== 0) return;
      // let real controls (arrows, dots, buttons, form fields) behave normally
      if (e.target.closest('button, input, textarea, select, [data-vd-bc-dot]')) return;

      m.on = true;
      m.moved = false;
      m.x0 = e.clientX;
      m.dx = 0;
      m.lastX = e.clientX;
      m.tLast = Date.now();
      m.vx = 0;

      clearTimeout(self._settle);
      self.normalise();
      self.track.classList.remove('is-animating');
    });

    document.addEventListener('mousemove', function (e) {
      if (!m.on) return;
      var dx = e.clientX - m.x0;

      if (!m.moved) {
        if (Math.abs(dx) < 5) return;   // a click, not a drag
        m.moved = true;
        self.root.classList.add('vd-bc--dragging');
      }

      e.preventDefault();

      var now = Date.now();
      var dt = Math.max(now - m.tLast, 1);
      m.vx = (e.clientX - m.lastX) / dt;
      m.lastX = e.clientX;
      m.tLast = now;

      m.dx = dx;
      self.track.style.transform =
        'translateX(' + (self.offset - self.index * self.step + dx) + 'px)';
    });

    document.addEventListener('mouseup', function () {
      if (!m.on) return;
      m.on = false;
      self.root.classList.remove('vd-bc--dragging');

      if (!m.moved) return;   // plain click: leave it to the link or card

      var dx = m.dx;
      m.dx = 0;
      self.settleDrag(dx, m.vx);

      // a drag ends in a click event on whatever sat under the cursor — swallow it
      self._noClick = true;
      setTimeout(function () { self._noClick = false; }, 60);
    });

    this.viewport.addEventListener('click', function (e) {
      if (!self._noClick) return;
      e.preventDefault();
      e.stopPropagation();
    }, true);

    // native image/link ghost drag fights the gesture
    this.viewport.addEventListener('dragstart', function (e) { e.preventDefault(); });

    /* ---- trackpad horizontal swipe ----
       Only claims the gesture when it is clearly sideways, so ordinary vertical
       scrolling still belongs to the page. */
    var acc = 0;
    var accTimer = null;

    this.viewport.addEventListener('wheel', function (e) {
      if (Math.abs(e.deltaX) <= Math.abs(e.deltaY)) return;
      e.preventDefault();

      acc += e.deltaX;
      if (Math.abs(acc) > WHEEL_STEP) {
        self.go(acc < 0 ? -1 : 1);
        acc = 0;
      }

      clearTimeout(accTimer);
      accTimer = setTimeout(function () { acc = 0; }, 220);
    }, { passive: false });

    window.addEventListener('resize', function () {
      clearTimeout(self._rz);
      self._rz = setTimeout(function () { self.layout(); self.apply(false); }, 150);
    });
  };

  VinodCarousel.prototype.layout = function () {
    var first = this.track.children[0];
    var styles = getComputedStyle(this.track);
    this.gap = parseFloat(styles.columnGap || styles.gap) || 0;
    // offsetWidth ignores the focus scale transform, so the step stays correct
    this.cardW = first.offsetWidth || first.getBoundingClientRect().width;
    this.step = this.cardW + this.gap;

    if (this.centered) {
      var pad = (this.viewport.clientWidth - this.cardW) / 2;
      this.offset = Math.max(pad, 0);
    } else {
      this.offset = 0;
    }
  };

  VinodCarousel.prototype.apply = function (animate) {
    this.track.classList.toggle('is-animating', !!animate);
    this.track.style.transform =
      'translateX(' + (this.offset - this.index * this.step) + 'px)';
    this.markActive();
    this.syncDots();
  };

  // Scale the card at the centre up and its neighbours back.
  VinodCarousel.prototype.markActive = function () {
    if (!this.centered) return;
    var kids = this.track.children;
    for (var i = 0; i < kids.length; i++) {
      var dist = Math.abs(i - this.index);
      kids[i].classList.toggle('is-active', dist === 0);
      kids[i].classList.toggle('is-near', dist === 1);
    }
  };

  VinodCarousel.prototype.syncDots = function () {
    var active = ((this.index % this.count) + this.count) % this.count;
    this.dots.forEach(function (dot, i) {
      dot.setAttribute('aria-current', i === active ? 'true' : 'false');
    });
  };

  VinodCarousel.prototype.go = function (dir) {
    var self = this;
    this.index += dir;
    this.apply(true);
    clearTimeout(this._settle);
    this._settle = setTimeout(function () { self.normalise(); }, 460);
    this.restart();
  };

  // Jump back into the middle copy without animating, so the loop is seamless.
  VinodCarousel.prototype.normalise = function () {
    if (this.index >= this.count * 2) this.index -= this.count;
    else if (this.index < this.count) this.index += this.count;
    else return;
    this.apply(false);
    var self = this;
    requestAnimationFrame(function () {
      requestAnimationFrame(function () { self.track.classList.add('is-animating'); });
    });
  };

  VinodCarousel.prototype.start = function () {
    if (!this.autoplay || this.timer) return;
    var self = this;
    this.timer = setInterval(function () {
      if (self.held || self.drag.on || self.root.dataset.spinning === 'true') return;
      self.go(1);
    }, this.speed);
  };

  VinodCarousel.prototype.stop = function () {
    clearInterval(this.timer);
    this.timer = null;
  };

  VinodCarousel.prototype.restart = function () {
    this.stop();
    this.start();
  };

  /* ---------------------------------------------------------------- wheel */

  function VinodWheel(root) {
    this.root = root;
    this.disc = root.querySelector('[data-vd-wheel-disc]');
    this.btn = root.querySelector('[data-vd-wheel-btn]');
    this.msg = root.querySelector('[data-vd-wheel-msg]');
    this.gate = root.querySelector('[data-vd-wheel-gate]');
    if (!this.disc || !this.btn) return;

    try {
      this.prizes = JSON.parse(root.dataset.prizes || '[]');
    } catch (e) {
      this.prizes = [];
    }
    if (!this.prizes.length) return;

    this.once = root.dataset.once === 'true';
    this.angle = 0;
    this.spinning = false;

    var saved = this.once ? this.read() : null;
    if (saved) this.reveal(saved, true);
    else this.btn.addEventListener('click', this.spin.bind(this));
  }

  VinodWheel.prototype.read = function () {
    try {
      var raw = window.localStorage.getItem(STORE_KEY);
      return raw ? JSON.parse(raw) : null;
    } catch (e) {
      return null;
    }
  };

  VinodWheel.prototype.write = function (prize) {
    try {
      window.localStorage.setItem(STORE_KEY, JSON.stringify(prize));
    } catch (e) {
      /* storage unavailable — prize simply is not remembered */
    }
  };

  VinodWheel.prototype.spin = function () {
    if (this.spinning) return;

    if (this.gate && !this.gate.checkValidity()) {
      this.gate.focus();
      this.gate.reportValidity && this.gate.reportValidity();
      return;
    }

    var seg = 360 / this.prizes.length;
    var i = Math.floor(Math.random() * this.prizes.length);
    var target = this.angle + 360 * 5 + (360 - (i * seg + seg / 2)) - (this.angle % 360);

    this.spinning = true;
    var carousel = this.root.closest('[data-vd-bc]');
    if (carousel) carousel.dataset.spinning = 'true';

    this.btn.disabled = true;
    this.btn.textContent = this.btn.dataset.spinningLabel || 'Spinning…';
    if (this.msg) this.msg.textContent = 'Good luck!';

    this.disc.style.transition = 'transform 4s cubic-bezier(0.17, 0.89, 0.13, 1)';
    this.disc.style.transform = 'rotate(' + target + 'deg)';
    this.angle = target;

    var self = this;
    var prize = this.prizes[i];
    setTimeout(function () {
      self.spinning = false;
      if (carousel) carousel.dataset.spinning = 'false';
      if (self.once) self.write(prize);
      self.reveal(prize, false);
    }, 4050);
  };

  VinodWheel.prototype.reveal = function (prize, restored) {
    var btn = this.btn;

    if (!prize.code) {
      if (this.msg) this.msg.textContent = this.root.dataset.loseMessage || prize.label;
      btn.disabled = false;
      btn.textContent = this.root.dataset.loseCta || 'Shop the sale';
      var shopUrl = this.root.dataset.loseUrl || '/collections/all';
      btn.addEventListener('click', function () { window.location.href = shopUrl; });
      return;
    }

    var tpl = restored
      ? this.root.dataset.restoredMessage || 'Your code [prize] ([code]) is ready to use.'
      : this.root.dataset.winMessage || 'You won [prize]. Code [code] applies at checkout.';

    if (this.msg) {
      this.msg.textContent = tpl
        .replace('[prize]', prize.label)
        .replace('[code]', prize.code);
    }

    // Shopify applies a discount code via /discount/CODE and then redirects.
    var link = document.createElement('a');
    link.className = btn.className;
    link.href =
      '/discount/' +
      encodeURIComponent(prize.code) +
      '?redirect=' +
      encodeURIComponent(this.root.dataset.redirect || '/collections/all');
    link.textContent = (this.root.dataset.applyLabel || 'Apply') + ' ' + prize.code;
    btn.parentNode.replaceChild(link, btn);

    if (this.gate) this.gate.setAttribute('hidden', 'hidden');
  };

  /* ------------------------------------------------------------- bootstrap */

  function init(scope) {
    var target = scope || document;
    Array.prototype.forEach.call(
      target.querySelectorAll('[data-vd-bc]:not([data-vd-ready])'),
      function (el) {
        el.setAttribute('data-vd-ready', 'true');
        new VinodCarousel(el);
        Array.prototype.forEach.call(
          el.querySelectorAll('[data-vd-wheel]:not([data-vd-clone] [data-vd-wheel])'),
          function (wheel) { new VinodWheel(wheel); }
        );
      }
    );
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { init(); });
  } else {
    init();
  }

  // Theme editor re-renders sections in place.
  document.addEventListener('shopify:section:load', function (e) { init(e.target); });
  document.addEventListener('shopify:section:unload', function (e) {
    Array.prototype.forEach.call(e.target.querySelectorAll('[data-vd-ready]'), function (el) {
      el.removeAttribute('data-vd-ready');
    });
  });
})();


// spin wheel js 
/* ============================================================
   VINOD — Spin & Win
   Card click -> modal (name / phone / email) -> Claim Now
   -> 10s spin, fast then easing to a stop -> coupon + copy.
   The wheel is drawn as SVG here (Liquid has no trigonometry),
   which gives real wedge separators, a bulb rim and crisp text.
   ============================================================ */
(function () {
  'use strict';

  var SPIN_MS = 10000;
  var TURNS = 12;
  var EASING = 'cubic-bezier(0.05, 0.72, 0.10, 1)'; // fast off the line, long glide
  var EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

  var CX = 100, CY = 100;      // viewBox is 0 0 200 200
  var R_FACE = 84;             // coloured face
  var R_RIM = 94;              // outer rim ring
  var R_BULB = 89;             // bulb ring

  function digits(v) { return (v || '').replace(/\D/g, ''); }

  function readJSON(el, attr, fallback) {
    try { return JSON.parse(el.getAttribute(attr)); } catch (e) { return fallback; }
  }

  function esc(s) {
    return String(s == null ? '' : s)
      .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
  }

  function fill(tpl, prize, code) {
    return String(tpl || '')
      .replace(/\[prize\]/g, prize || '')
      .replace(/\[code\]/g, code || '');
  }

  /* ---------- SVG wheel ---------- */

  function polar(angleDeg, r) {
    var rad = (angleDeg - 90) * Math.PI / 180;
    return [CX + r * Math.cos(rad), CY + r * Math.sin(rad)];
  }

  function n2(v) { return Math.round(v * 100) / 100; }

  function wedgePath(a0, a1, r) {
    var p0 = polar(a0, r), p1 = polar(a1, r);
    var large = (a1 - a0) > 180 ? 1 : 0;
    return 'M' + CX + ',' + CY +
      ' L' + n2(p0[0]) + ',' + n2(p0[1]) +
      ' A' + r + ',' + r + ' 0 ' + large + ',1 ' + n2(p1[0]) + ',' + n2(p1[1]) + ' Z';
  }

  function buildWheel(prizes, c, uid) {
    var n = prizes.length;
    var seg = 360 / n;
    var segColors = [c.s1, c.s2, c.s3, c.s4];
    var textColors = [c.tLight, c.tDark, c.tLight, c.tDark];

    // avoid the last wedge matching the first when n isn't a multiple of 4
    function ci(i) {
      var k = i % 4;
      if (i === n - 1 && k === 0) k = 2;
      return k;
    }

    var wedges = '', labels = '';
    for (var i = 0; i < n; i++) {
      var a0 = i * seg, a1 = (i + 1) * seg, k = ci(i);
      wedges += '<path d="' + wedgePath(a0, a1, R_FACE) + '" fill="' + segColors[k] +
        '" stroke="rgba(255,255,255,.9)" stroke-width="1.1"/>';

      // radial label, flipped on the left half so it never reads upside down
      var mid = a0 + seg / 2;
      var flip = mid > 180;
      var rot = flip ? mid + 90 : mid - 90;
      var anchor = flip ? 'start' : 'end';
      var x = flip ? CX - (R_FACE - 8) : CX + (R_FACE - 8);
      var size = n > 10 ? 6.6 : n > 8 ? 7.4 : 8.2;
      var text = prizes[i].label;
      if (text.length > 20) text = text.slice(0, 19) + '…';

      labels += '<text x="' + n2(x) + '" y="' + CY + '" class="vd-sw-seg-label" ' +
        'font-size="' + size + '" fill="' + textColors[k] + '" text-anchor="' + anchor + '" ' +
        'transform="rotate(' + n2(rot) + ' ' + CX + ' ' + CY + ')">' + esc(text) + '</text>';
    }

    // rim bulbs
    var bulbCount = Math.min(24, Math.max(12, n * 2));
    var bulbs = '';
    for (var j = 0; j < bulbCount; j++) {
      var p = polar(j * (360 / bulbCount), R_BULB);
      bulbs += '<circle class="vd-sw-bulb" cx="' + n2(p[0]) + '" cy="' + n2(p[1]) +
        '" r="2.1" fill="' + c.bulb + '"/>';
    }

    return '' +
      '<svg class="vd-sw-wheel" viewBox="0 0 200 200" role="img" aria-label="Prize wheel">' +
        '<defs>' +
          '<radialGradient id="vdGloss' + uid + '" cx="34%" cy="26%" r="72%">' +
            '<stop offset="0%" stop-color="#fff" stop-opacity=".30"/>' +
            '<stop offset="55%" stop-color="#fff" stop-opacity=".05"/>' +
            '<stop offset="100%" stop-color="#000" stop-opacity=".18"/>' +
          '</radialGradient>' +
          '<linearGradient id="vdRim' + uid + '" x1="0" y1="0" x2="0" y2="1">' +
            '<stop offset="0%" stop-color="' + c.rimHi + '"/>' +
            '<stop offset="50%" stop-color="' + c.rim + '"/>' +
            '<stop offset="100%" stop-color="' + c.rimHi + '"/>' +
          '</linearGradient>' +
          '<filter id="vdShadow' + uid + '" x="-25%" y="-25%" width="150%" height="150%">' +
            '<feDropShadow dx="0" dy="3" stdDeviation="4" flood-color="#000" flood-opacity=".38"/>' +
          '</filter>' +
        '</defs>' +

        '<g filter="url(#vdShadow' + uid + ')">' +
          '<circle cx="100" cy="100" r="' + R_RIM + '" fill="url(#vdRim' + uid + ')"/>' +
        '</g>' +
        '<g class="vd-sw-bulbs">' + bulbs + '</g>' +

        '<g class="vd-sw-disc-rot" data-vd-sw-disc>' +
          wedges +
          labels +
        '</g>' +

        '<circle cx="100" cy="100" r="' + R_FACE + '" fill="url(#vdGloss' + uid + ')" pointer-events="none"/>' +

        // hub
        '<circle cx="100" cy="100" r="15" fill="#fff"/>' +
        '<circle cx="100" cy="100" r="10.5" fill="' + c.rim + '"/>' +
        '<circle cx="100" cy="100" r="4.4" fill="#fff"/>' +

        // pointer, tucked into the rim at 12 o'clock
        '<g filter="url(#vdShadow' + uid + ')">' +
          '<path d="M100 31 L86 5.5 Q100 -2.5 114 5.5 Z" fill="' + c.pin + '" ' +
            'stroke="rgba(0,0,0,.22)" stroke-width="1"/>' +
          '<circle cx="100" cy="8" r="4.2" fill="' + c.pinDot + '"/>' +
        '</g>' +
      '</svg>';
  }

  // Long labels ("Free shipping") must not run past the hub. Measure once the
  // wheel is actually visible and compress anything that overruns.
  function fitLabels(root) {
    var max = R_FACE - 8 - 20;
    var nodes = root.querySelectorAll('.vd-sw-seg-label');
    for (var i = 0; i < nodes.length; i++) {
      var t = nodes[i], len = 0;
      try { len = t.getComputedTextLength(); } catch (e) { continue; }
      if (len > max) {
        t.setAttribute('textLength', max);
        t.setAttribute('lengthAdjust', 'spacingAndGlyphs');
      }
    }
  }

  /* ---------- page lock ---------- */

  var lockCount = 0, savedScroll = 0;

  function lockPage() {
    if (lockCount++ > 0) return;
    savedScroll = window.pageYOffset || document.documentElement.scrollTop;
    var sbw = window.innerWidth - document.documentElement.clientWidth;
    if (sbw > 0) document.body.style.paddingRight = sbw + 'px';
    document.documentElement.classList.add('vd-sw-locked');
  }

  function unlockPage() {
    if (--lockCount > 0) return;
    lockCount = 0;
    document.documentElement.classList.remove('vd-sw-locked');
    document.body.style.paddingRight = '';
    window.scrollTo(0, savedScroll);
  }

  /* ---------- copy ---------- */

  function copyText(text) {
    if (navigator.clipboard && navigator.clipboard.writeText) {
      return navigator.clipboard.writeText(text);
    }
    return new Promise(function (resolve, reject) {
      try {
        var ta = document.createElement('textarea');
        ta.value = text;
        ta.setAttribute('readonly', '');
        ta.style.position = 'fixed';
        ta.style.left = '-9999px';
        document.body.appendChild(ta);
        ta.select();
        ta.setSelectionRange(0, 99999);
        document.execCommand('copy');
        document.body.removeChild(ta);
        resolve();
      } catch (err) { reject(err); }
    });
  }

  /* ---------- one instance ---------- */

  function init(card) {
    if (card.dataset.vdSwReady === '1') return;
    card.dataset.vdSwReady = '1';

    var id = card.getAttribute('data-vd-sw');
    var modal = document.querySelector('[data-vd-sw-modal="' + id + '"]');
    if (!modal) return;

    // The carousel track is transformed, which breaks position:fixed for
    // anything inside it. Move the modal to <body>.
    if (modal.parentNode !== document.body) document.body.appendChild(modal);

    var prizes = readJSON(modal, 'data-prizes', []);
    var colors = readJSON(modal, 'data-colors', null);
    if (!prizes.length || !colors) return;

    var uid = id.replace(/[^a-z0-9]/gi, '');
    var storeKey = 'vd-sw:' + id;
    var once = modal.getAttribute('data-once') === 'true';
    var redirect = modal.getAttribute('data-redirect') || '/collections/all';

    var msg = {
      win: modal.getAttribute('data-win-message') || '',
      restored: modal.getAttribute('data-restored-message') || '',
      lose: modal.getAttribute('data-lose-message') || '',
      loseCta: modal.getAttribute('data-lose-cta') || 'Shop now',
      spinning: modal.getAttribute('data-spinning-label') || 'Good luck…',
      copied: modal.getAttribute('data-copied-label') || 'Copied'
    };

    // paint both wheels
    var cardStage = card.querySelector('[data-vd-sw-card-wheel]');
    if (cardStage) {
      cardStage.innerHTML = buildWheel(prizes, colors, uid + 'c');
      cardStage.querySelector('.vd-sw-wheel').classList.add('vd-sw-wheel--card');
      fitLabels(cardStage);
    }
    var modalStage = modal.querySelector('[data-vd-sw-stage]');
    modalStage.innerHTML = buildWheel(prizes, colors, uid + 'm');

    var disc = modalStage.querySelector('[data-vd-sw-disc]');
    var stepForm = modal.querySelector('[data-vd-sw-step="form"]');
    var stepWheel = modal.querySelector('[data-vd-sw-step="wheel"]');
    var stepResult = modal.querySelector('[data-vd-sw-step="result"]');
    var form = modal.querySelector('[data-vd-sw-form]');
    var submitBtn = modal.querySelector('[data-vd-sw-submit]');
    var status = modal.querySelector('[data-vd-sw-status]');
    var prizeEl = modal.querySelector('[data-vd-sw-prize]');
    var codeWrap = modal.querySelector('[data-vd-sw-code-wrap]');
    var codeEl = modal.querySelector('[data-vd-sw-code]');
    var copyBtn = modal.querySelector('[data-vd-sw-copy]');
    var copyLabel = modal.querySelector('[data-vd-sw-copy-label]');
    var applyBtn = modal.querySelector('[data-vd-sw-apply]');
    var resultMsg = modal.querySelector('[data-vd-sw-result-msg]');
    var closeBtn = modal.querySelector('[data-vd-sw-close]');
    var backdrop = modal.querySelector('[data-vd-sw-backdrop]');

    var seg = 360 / prizes.length;
    var spinning = false;
    var lastOpener = null;

    function show(step) {
      [stepForm, stepWheel, stepResult].forEach(function (el) {
        if (el) el.classList.add('vd-sw-hidden');
      });
      if (step) step.classList.remove('vd-sw-hidden');
      modal.classList.toggle('is-wheel', step === stepWheel);
    }

    /* ---------- open / close ---------- */

    function open() {
      lastOpener = document.activeElement;
      modal.hidden = false;
      lockPage();

      var saved = null;
      if (once) {
        try { saved = JSON.parse(localStorage.getItem(storeKey)); } catch (e) {}
      }

      if (saved && saved.label) {
        renderResult(saved.label, saved.code, msg.restored);
      } else {
        show(stepForm);
        var first = form.querySelector('input');
        if (first) setTimeout(function () { first.focus(); }, 60);
      }
    }

    function close() {
      if (spinning) return; // never yank the wheel away mid-spin
      modal.hidden = true;
      modal.classList.remove('is-wheel', 'is-spinning');
      unlockPage();
      if (lastOpener && lastOpener.focus) lastOpener.focus();
    }

    card.addEventListener('click', function (e) { e.preventDefault(); open(); });
    if (closeBtn) closeBtn.addEventListener('click', close);
    if (backdrop) backdrop.addEventListener('click', close);

    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && !modal.hidden) close();
    });

    modal.addEventListener('keydown', function (e) {
      if (e.key !== 'Tab') return;
      var list = Array.prototype.filter.call(
        modal.querySelectorAll('button:not([disabled]), input, a[href], [tabindex]:not([tabindex="-1"])'),
        function (el) { return el.offsetParent !== null; }
      );
      if (!list.length) return;
      var first = list[0], last = list[list.length - 1];
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    });

    /* ---------- validation ---------- */

    function setError(field, message) {
      var wrap = field.closest('.vd-sw-field');
      if (!wrap) return;
      var err = wrap.querySelector('.vd-sw-field__err');
      if (message) { wrap.classList.add('has-error'); if (err) err.textContent = message; }
      else wrap.classList.remove('has-error');
    }

    function validate() {
      var ok = true;
      var name = form.elements.vd_name;
      var phone = form.elements.vd_phone;
      var email = form.elements.vd_email;

      if (name.value.trim().length < 2) { setError(name, 'Please enter your full name.'); ok = false; }
      else setError(name, '');

      var d = digits(phone.value);
      if (d.length < 10 || d.length > 15) { setError(phone, 'Enter a valid 10-digit mobile number.'); ok = false; }
      else setError(phone, '');

      if (!EMAIL_RE.test(email.value.trim())) { setError(email, 'Enter a valid email address.'); ok = false; }
      else setError(email, '');

      return ok;
    }

    ['vd_name', 'vd_phone', 'vd_email'].forEach(function (n) {
      var el = form.elements[n];
      if (el) el.addEventListener('input', function () { setError(el, ''); });
    });

    /* ---------- claim + spin ---------- */

    form.addEventListener('submit', function (e) {
      e.preventDefault();
      if (spinning) return;
      if (!validate()) return;

      var lead = {
        name: form.elements.vd_name.value.trim(),
        phone: digits(form.elements.vd_phone.value),
        email: form.elements.vd_email.value.trim(),
        section: id,
        page: window.location.pathname,
        at: new Date().toISOString()
      };

      // ── LEAD HOOK ─────────────────────────────────────
      // Nothing is sent anywhere yet, by design. To wire a
      // destination later, replace this block with one fetch:
      //
      //   fetch(window.VD_LEAD_ENDPOINT, {
      //     method: 'POST',
      //     headers: { 'content-type': 'application/json' },
      //     body: JSON.stringify(lead)
      //   });
      //
      // Do NOT hardcode the URL here — read it from a theme
      // setting so it can be rotated without a theme edit.
      window.VD_LAST_LEAD = lead;
      // ─────────────────────────────────────────────

      submitBtn.disabled = true;
      show(stepWheel);
      spin();
    });

    function spin() {
      spinning = true;
      fitLabels(modalStage);   // now visible, so text can be measured
      modal.classList.add('is-spinning');
      if (status) status.textContent = msg.spinning;

      var index = Math.floor(Math.random() * prizes.length);
      var landing = 360 - (index * seg + seg / 2);
      var target = TURNS * 360 + landing;

      disc.style.transition = 'none';
      disc.style.transform = 'rotate(0deg)';
      void disc.getBoundingClientRect();   // force a frame

      disc.style.transition = 'transform ' + SPIN_MS + 'ms ' + EASING;
      disc.style.transform = 'rotate(' + target + 'deg)';

      var done = false;
      function finish() {
        if (done) return;
        done = true;
        spinning = false;
        modal.classList.remove('is-spinning');
        var p = prizes[index];
        if (once) {
          try { localStorage.setItem(storeKey, JSON.stringify(p)); } catch (e) {}
        }
        renderResult(p.label, p.code, msg.win);
      }

      disc.addEventListener('transitionend', finish, { once: true });
      setTimeout(finish, SPIN_MS + 400); // belt and braces
    }

    /* ---------- result ---------- */

    function renderResult(label, code, template) {
      show(stepResult);
      prizeEl.textContent = label;

      if (code && code.trim()) {
        var c = code.trim();
        codeWrap.classList.remove('vd-sw-hidden');
        codeEl.textContent = c;
        applyBtn.textContent = modal.getAttribute('data-apply-label') || 'Apply & shop';
        applyBtn.href = '/discount/' + encodeURIComponent(c) +
          '?redirect=' + encodeURIComponent(redirect);
        resultMsg.textContent = fill(template, label, c);
      } else {
        codeWrap.classList.add('vd-sw-hidden');
        applyBtn.textContent = msg.loseCta;
        applyBtn.href = redirect;
        resultMsg.textContent = msg.lose;
      }
    }

    if (copyBtn) {
      copyBtn.addEventListener('click', function () {
        var code = codeEl.textContent.trim();
        if (!code) return;
        copyText(code).then(function () {
          copyBtn.classList.add('is-copied');
          var original = copyLabel.textContent;
          copyLabel.textContent = msg.copied;
          setTimeout(function () {
            copyBtn.classList.remove('is-copied');
            copyLabel.textContent = original;
          }, 1600);
        });
      });
    }
  }

  function boot() { document.querySelectorAll('[data-vd-sw]').forEach(init); }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  document.addEventListener('shopify:section:load', boot);
})();
